import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_SEARCH_DEBOUNCE_MS = 500;

type UseDebouncedSearchParams = {
  debounceMs?: number;
  onSearch?: (search: string) => void;
};

type UseDebouncedSearchReturn = {
  search: string;
  setSearch: (value: string) => void;
  onInputValueChange: (value: string) => void;
  cancel: () => void;
};

export const useDebouncedSearch = ({
  debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  onSearch,
}: UseDebouncedSearchParams = {}): UseDebouncedSearchReturn => {
  const [search, setSearch] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        onSearch?.(value);
      }, debounceMs),
    [debounceMs, onSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onInputValueChange = (value: string) => {
    debouncedSearch(value);
  };

  return {
    search,
    setSearch,
    onInputValueChange,
    cancel: debouncedSearch.cancel,
  };
};
